import { AnyAction } from 'redux';
import {
  Anrede,
  Bankverbindung as BankverbindungCrm,
  BankverbindungVorvertrag,
  Domaenenwert,
  EinstiegResponse,
  Vertreter as VermittlerBff,
  Verwendungszweck,
  Zahlungsart,
  Zahlweise,
} from '@ps20/bff-models';
import {
  AdressvorschlagFailedPayload,
  AdressvorschlagPayload,
  CALLER_ID,
  GlobalActions,
  isMaklerbetreuerOrMaklerOrUvm,
  ZahlungsoptionenChanged,
} from '@ps20-frontend/shop-integration-redux';
import cloneDeep from 'lodash/cloneDeep';
import {
  Adressvorschlag,
  convertSafeDomaenenwert,
  formatBanknameFromString,
  formatIban,
} from '@ps20-frontend/frontend-models';
import { IAppState } from '@ps20-frontend/apps/checkout-store';
import { CheckoutActions } from '../checkout.actions';
import { Bankverbindung, formatGeburtsdatum } from './versicherungsnehmer.reducer';

export interface Adresse {
  strasse: string;
  hausnummer: string;
  plz: string;
  ort: string;
  land?: Domaenenwert;
}

export interface Kontoinhaber {
  kundenverwaltungID: string;
  vorname: string;
  nachname: string;
  firmenname: string;
  businessForm: Domaenenwert;
  juristisch: boolean;
  anrede: Anrede;
  akadgrad?: Domaenenwert;
  adelstitel?: Domaenenwert;
  adelsbez?: Domaenenwert;
  namenszusatz?: Domaenenwert;
  famstand?: Domaenenwert;
  geburtsdatum: string;
  staatsbschaft: Domaenenwert;
  heimatort: string;
  geburtsland?: Domaenenwert;
  kundennummer: string;
  adresse: Adresse;
  bankverbindungen: Bankverbindung[];
}
export interface Zahlung {
  zahlungsart: Zahlungsart;
  zahlweise: Zahlweise;
  iban: string;
  bic: string;
  bankname: string;
  bankort: string;
  sepaErteilt: boolean;
  vorbelegt: boolean;
  manuelleIbanEingabe: boolean;
  verwendungszweck: Verwendungszweck;
  showValidationError: boolean;
  kontoinhaber: Kontoinhaber;
  azam: {
    abweichenderAzamKontoinhaber?: Kontoinhaber;
    staatsangehoerigkeiten?: Domaenenwert[];
    adressvorschlag?: Adressvorschlag;
  };
}

export const mapCrmBankverbindungenToBankverbindung = (
  crmBankverbindungen: BankverbindungCrm[],
): Bankverbindung[] => {
  const mappedBankverbindungen = crmBankverbindungen.map(crmBankverbindung => ({
    ...crmBankverbindung,
    iban: valueOrDefault(crmBankverbindung.iban && formatIban(crmBankverbindung.iban), null),
    bankOrt: '',
    sepamandatErteilt: false,
  }));

  return mappedBankverbindungen.filter(bankverbindung => bankverbindung.iban);
};

export const zahlungReducer = function (
  sectionState: Zahlung = null,
  action: AnyAction,
  state: IAppState,
): Zahlung {
  switch (action.type) {
    case GlobalActions.ZAHLUNGSOPTIONEN_CHANGED:
    case GlobalActions.ZAHLUNGSOPTIONEN_CHANGED_VORVERTRAG: {
      const newState: Zahlung = cloneDeep(sectionState);
      const zahlungsoptionenChanged: ZahlungsoptionenChanged = action.payload;
      newState.zahlweise = zahlungsoptionenChanged.zahlungsweise
        ? zahlungsoptionenChanged.zahlungsweise
        : newState.zahlweise;
      newState.zahlungsart = zahlungsoptionenChanged.zahlungsart
        ? zahlungsoptionenChanged.zahlungsart
        : newState.zahlungsart;
      return newState;
    }
    case CheckoutActions.ZAHLUNG_IBAN_VALIDATED:
    case CheckoutActions.ZAHLUNG_IBAN_CHANGED: {
      return {
        ...sectionState,
        iban: action.payload.iban,
      };
    }
    case CheckoutActions.ZAHLUNG_MANUELLE_IBAN_EINGABE: {
      return {
        ...sectionState,
        manuelleIbanEingabe: action.payload.changed,
        bankname: '',
        bic: '',
      };
    }
    case CheckoutActions.ZAHLUNG_BIC_CHANGED: {
      return {
        ...sectionState,
        bic: action.payload.bic,
      };
    }
    case CheckoutActions.ZAHLUNG_VERWENDUNGSZWECK_CHANGED: {
      return {
        ...sectionState,
        verwendungszweck: action.payload.verwendungszweck,
      };
    }
    case CheckoutActions.BANKDATEN_RECEIVED: {
      const receivedIban = action.payload.iban?.replace(/\s/g, '') ?? '';
      const currentIban = sectionState.iban?.replace(/\s/g, '') ?? '';
      if (receivedIban !== currentIban) {
        return sectionState;
      }
      return {
        ...sectionState,
        iban: action.payload.iban,
        bankname: action.payload.bankname,
        bic: action.payload.bic,
        showValidationError: false,
      };
    }
    case CheckoutActions.BANKDATEN_NOT_RECEIVED: {
      return {
        ...sectionState,
        bankname: '',
        showValidationError: false,
      };
    }
    case CheckoutActions.BANKDATEN_FAILED: {
      return {
        ...sectionState,
        bankname: '',
        showValidationError: true,
      };
    }
    case CheckoutActions.CHANGE_AZAM_ABWEICHENDER_KONTOINHABER: {
      const newState: Zahlung = cloneDeep(sectionState);
      newState.kontoinhaber = newState.azam.abweichenderAzamKontoinhaber;
      return {
        ...newState,
        iban: '',
        bic: '',
        bankname: '',
        manuelleIbanEingabe: true,
      };
    }
    case CheckoutActions.RESET_AZAM_KONTOINHABER: {
      const newState: Zahlung = cloneDeep(sectionState);
      newState.azam.abweichenderAzamKontoinhaber = null;
      return newState;
    }

    case CheckoutActions.CHANGE_KONTOINHABER: {
      const kontoinhaber: Kontoinhaber = action.payload;
      return {
        ...sectionState,
        kontoinhaber,
        iban: '',
        bic: '',
        bankname: '',
      };
    }
    case GlobalActions.EINSTIEG_REQUEST_SUCCESSFUL: {
      const newState: Zahlung = cloneDeep(sectionState);
      const vertreter = action.payload.vertreter as VermittlerBff;
      if (vertreter.isInkasso && isMaklerbetreuerOrMaklerOrUvm(vertreter.role)) {
        return {
          ...sectionState,
          zahlungsart: Zahlungsart.MAKLER_INKASSO,
        };
      }
      if (
        action.payload.versicherungsnehmer.bankverbindungen &&
        action.payload.versicherungsnehmer.bankverbindungen.length > 0 &&
        !state.kdv.azamCryptId
      ) {
        const bankverbindung: BankverbindungCrm =
          action.payload.versicherungsnehmer.bankverbindungen[0];
        if (bankverbindung) {
          newState.iban = valueOrDefault(bankverbindung.iban, sectionState.iban);
          newState.bic = valueOrDefault(bankverbindung.bic, sectionState.bic);
          newState.vorbelegt = true;
        }
      }

      setAbweichenderKontoinhaber(newState, action.payload.versicherungsnehmer);
      return newState;
    }
    case CheckoutActions.STAATSBUERGERSCHAFTEN_RECEIVED: {
      const newState: Zahlung = cloneDeep(sectionState);
      newState.azam.staatsangehoerigkeiten = action.payload;
      return newState;
    }

    case CheckoutActions.ADOPT_CURRENT_KONTOINHABER_TO_AZAM_ABWEICHENDER_KONTOINAHBER: {
      const newState: Zahlung = cloneDeep(sectionState);
      const aktuellerKontoinhaber = cloneDeep(newState.kontoinhaber);

      aktuellerKontoinhaber.adresse.land = {
        text: 'Deutschland',
        wert: 'DE',
      };

      newState.azam.abweichenderAzamKontoinhaber = {
        ...aktuellerKontoinhaber,
        kundennummer: '',
        kundenverwaltungID: '',
        geburtsland: null,
        heimatort: '',
        namenszusatz: null,
        adelsbez: null,
        adelstitel: null,
        famstand: null,
        akadgrad: null,
        bankverbindungen: [],
      };
      return newState;
    }

    case CheckoutActions.AZAM_PARTIAL_KONTOINHABER_UPDATE: {
      const newState: Zahlung = cloneDeep(sectionState);
      const changes: Partial<Kontoinhaber> = action.payload;
      newState.azam.abweichenderAzamKontoinhaber = {
        ...newState.azam.abweichenderAzamKontoinhaber,
        ...changes,
      };
      return newState;
    }

    case CheckoutActions.AZAM_PARTIAL_KONTOINHABER_ADDRESS_UPDATE: {
      const newState: Zahlung = cloneDeep(sectionState);
      const addressChanges: Partial<Adresse> = action.payload;
      newState.azam.abweichenderAzamKontoinhaber.adresse = {
        ...newState.azam.abweichenderAzamKontoinhaber.adresse,
        ...addressChanges,
      };
      return newState;
    }
    case GlobalActions.ADDRESSVORSCHLAG_SUCCESSFUL: {
      const payload: AdressvorschlagPayload = action.payload;
      if (payload.callerId !== CALLER_ID.CHECKOUT_AZAM_ABWEICHENDER_KONTOINHABER) {
        return sectionState;
      }
      const newState: Zahlung = cloneDeep(sectionState);
      newState.azam.adressvorschlag = {
        lastError: null,
        orte: payload.orte,
        strassen: payload.strassen,
      };
      if (payload.orte.length === 1) {
        newState.azam.abweichenderAzamKontoinhaber.adresse.ort = payload.orte[0];
      }
      return newState;
    }
    case GlobalActions.ADDRESSVORSCHLAG_FAILED: {
      const payload: AdressvorschlagFailedPayload = action.payload;
      if (payload.callerId !== CALLER_ID.CHECKOUT_AZAM_ABWEICHENDER_KONTOINHABER) {
        return sectionState;
      }
      const newState: Zahlung = cloneDeep(sectionState);
      newState.azam.adressvorschlag = {
        lastError: payload.reason,
        orte: [],
        strassen: [],
      };
      return newState;
    }

    case CheckoutActions.AZAM_ABWEICHENDER_KONTOINHABER_PLZ_CHANGED: {
      const newState: Zahlung = cloneDeep(sectionState);
      newState.azam.adressvorschlag = {
        lastError: null,
        orte: [],
        strassen: [],
      };
      newState.azam.abweichenderAzamKontoinhaber.adresse = {
        plz: action.payload.plz,
        strasse: '',
        hausnummer: '',
        ort: '',
        land: {
          text: 'Deutschland',
          wert: 'DE',
        },
      };
      return newState;
    }
    case GlobalActions.UPDATE_VORVERTRAEGE: {
      return handleUpdateVorvertraege(sectionState, action);
    }
    default:
      return sectionState;
  }
};

function handleUpdateVorvertraege(sectionState: Zahlung, action: AnyAction): Zahlung {
  const newState = cloneDeep(sectionState);
  const vorvertragBvs: Array<BankverbindungVorvertrag> = action.payload.bankverbindungen ?? [];
  if (newState.kontoinhaber) {
    newState.kontoinhaber.bankverbindungen = mergeBankverbindungen(
      vorvertragBvs,
      newState.kontoinhaber.bankverbindungen,
    );
  }
  return { ...newState, ...buildVorvertragPrefill(vorvertragBvs) };
}

function mergeBankverbindungen(
  vorvertragBvs: Array<BankverbindungVorvertrag>,
  crmBvs: Array<Bankverbindung> | null | undefined,
): Array<Bankverbindung> {
  const merged: Array<Bankverbindung> = [];
  const seen = new Set<string>();

  vorvertragBvs.forEach(v => {
    const iban = formatIban(v.iban);
    const key = `${iban}|${v.swiftCode}`;
    if (!seen.has(key)) {
      seen.add(key);
      merged.push({ iban, bic: v.swiftCode, bankOrt: '', sepamandatErteilt: false } as Bankverbindung);
    }
  });

  (crmBvs ?? []).forEach(c => {
    const key = `${c.iban}|${c.bic}`;
    if (!seen.has(key)) {
      seen.add(key);
      merged.push(c);
    }
  });

  return merged;
}

function buildVorvertragPrefill(vorvertragBvs: Array<BankverbindungVorvertrag>): Partial<Zahlung> {
  const distinctIbans = new Set(vorvertragBvs.map(bv => formatIban(bv.iban)));

  if (distinctIbans.size === 1) {
    const first = vorvertragBvs[0];
    return {
      iban: formatIban(first.iban),
      bic: first.swiftCode,
      bankname: formatBanknameFromString(first.bankname),
      showValidationError: false,
      vorbelegt: true,
    };
  }
  if (distinctIbans.size > 1) {
    return { iban: '', bic: '', bankname: '', showValidationError: false, vorbelegt: false };
  }
  return {};
}

function setAbweichenderKontoinhaber(
  state: Zahlung,
  versicherungsnehmer: EinstiegResponse['versicherungsnehmer'],
): void {
  const mappedBankverbindungen = mapCrmBankverbindungenToBankverbindung(
    valueOrDefault(versicherungsnehmer.bankverbindungen, []),
  );
  state.kontoinhaber = {
    kundennummer: valueOrDefault(versicherungsnehmer.kundennummer, ''),
    kundenverwaltungID: valueOrDefault(versicherungsnehmer.kundenverwaltungID, ''),
    vorname: valueOrDefault(versicherungsnehmer.vorname, ''),
    nachname: valueOrDefault(versicherungsnehmer.nachname, ''),
    firmenname: valueOrDefault(versicherungsnehmer.firmenname, ''),
    businessForm: convertSafeDomaenenwert(versicherungsnehmer.businessForm),
    juristisch: versicherungsnehmer.juristisch,
    anrede: versicherungsnehmer.anrede,
    geburtsland: convertSafeDomaenenwert(versicherungsnehmer.geburtsland),
    heimatort: versicherungsnehmer.heimatort,
    namenszusatz: convertSafeDomaenenwert(versicherungsnehmer.namenszusatz),
    staatsbschaft: convertSafeDomaenenwert(versicherungsnehmer.staatsbschaft),
    adelsbez: convertSafeDomaenenwert(versicherungsnehmer.adelsbez),
    adelstitel: convertSafeDomaenenwert(versicherungsnehmer.adelstitel),
    famstand: convertSafeDomaenenwert(versicherungsnehmer.famstand),
    geburtsdatum: formatGeburtsdatum(versicherungsnehmer.geburtsdatum),
    akadgrad: convertSafeDomaenenwert(versicherungsnehmer.akadgrad),
    bankverbindungen: mappedBankverbindungen,
    adresse: {
      hausnummer: '',
      ort: '',
      plz: '',
      strasse: '',
      land: null,
    },
  };
  if (versicherungsnehmer.adresse) {
    state.kontoinhaber.adresse = {
      hausnummer: valueOrDefault(versicherungsnehmer.adresse.hausnummer, ''),
      ort: valueOrDefault(versicherungsnehmer.adresse.ort, ''),
      plz: valueOrDefault(versicherungsnehmer.adresse.plz, ''),
      strasse: valueOrDefault(versicherungsnehmer.adresse.strasse, ''),
      land: convertSafeDomaenenwert(versicherungsnehmer.adresse.land),
    };
  }
}

function valueOrDefault<T>(value: T, defaultValue: T): T {
  return value || defaultValue;
}
